import { useMemo, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { FormDatePicker, FormSelectField, FormTextField } from "@covertree/ui";
import { InputLabelHeight } from "@covertree/types";

import { communityConfigurationCardSx } from "./communityConfigurationStyles";

import type { CommunityConfigurationTaxDefault } from "@graphql/api";
import type { CommunityConfigurationFormValues } from "./communitiesConfigurationForm";

interface CommunitiesSectionProps {
  taxDefaults: CommunityConfigurationTaxDefault[];
}

export function CommunitiesSection({ taxDefaults }: CommunitiesSectionProps) {
  const { control, getValues, setValue } =
    useFormContext<CommunityConfigurationFormValues>();
  const [search, setSearch] = useState("");
  const communities = useWatch({ control, name: "communities" }) ?? [];
  const owners = useWatch({ control, name: "owners" }) ?? [];
  // Rent Manager does not provide an addendum date - it is set per community
  // and applied only to the residents imported when the configuration is activated
  const isRentManager = !!useWatch({ control, name: "rentManagerSettings" });
  const ownerOptions = useMemo(
    () => [
      { label: "Not assigned", value: "" },
      ...owners.map((owner) => ({
        label: owner.name || "Unnamed owner",
        value: owner.clientId,
      })),
    ],
    [owners]
  );

  const visibleCommunities = useMemo(() => {
    const query = search.trim().toLowerCase();
    return communities
      .map((community, index) => ({ community, index }))
      .filter(
        ({ community }) =>
          !query ||
          [
            community.name,
            community.displayId,
            community.ctmhcId,
            community.state,
          ].some((value) => value?.toLowerCase().includes(query))
      );
  }, [communities, search]);

  const assignOwner = (communityIndex: number, ownerClientId: string) => {
    if (!ownerClientId) return;

    const community = getValues(`communities.${communityIndex}`);
    const states = getValues("states");
    const stateIndex = states.findIndex(
      ({ state }) => state === community.state
    );
    if (stateIndex < 0) return;

    const state = states[stateIndex];
    const policyNumbers = state.policyNumbers.some(
      (policyNumber) => policyNumber.ownerClientId === ownerClientId
    )
      ? state.policyNumbers
      : [...state.policyNumbers, { ownerClientId, policyNumber: "" }];
    const otherFee = taxDefaults.find(
      (taxDefault) => taxDefault.state === community.state
    )?.otherFee;
    const localTaxes =
      !otherFee ||
      state.localTaxes.some(
        (localTax) => localTax.ownerClientId === ownerClientId
      )
        ? state.localTaxes
        : [
            ...state.localTaxes,
            {
              cityName: "",
              cityTaxRate: null,
              countyName: "",
              countyTaxRate: null,
              otherLocalTaxes: [{ name: otherFee.name, rate: otherFee.rate }],
              ownerClientId,
            },
          ];

    setValue(
      `states.${stateIndex}`,
      { ...state, localTaxes, policyNumbers },
      { shouldDirty: true }
    );
  };

  return (
    <Stack sx={communityConfigurationCardSx} spacing={2}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        gap={2}>
        <Typography variant="h4">Communities</Typography>
        <TextField
          size="small"
          label="Search communities"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Community</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>CTMHCID</TableCell>
              <TableCell>Legal entity owner</TableCell>
              {isRentManager && (
                <TableCell>
                  <Tooltip title="Applied only to the residents imported when the configuration is activated. Residents added later get no addendum date.">
                    <span>Default addendum date</span>
                  </Tooltip>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleCommunities.map(({ community, index }) => (
              <TableRow key={community.displayId}>
                <TableCell>
                  <Typography fontWeight={600}>{community.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {community.addressLine1}, {community.city} ·{" "}
                    {community.displayId}
                  </Typography>
                </TableCell>
                <TableCell>{community.state}</TableCell>
                <TableCell>
                  <Controller
                    control={control}
                    name={`communities.${index}.isActive`}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        inputProps={{
                          "aria-label": `Activate ${community.name}`,
                        }}
                        onChange={(_, checked) => field.onChange(checked)}
                      />
                    )}
                  />
                </TableCell>
                <TableCell>
                  <FormTextField
                    control={control}
                    hiddenLabel
                    id={`community-${index}-ctmhc-id`}
                    inputProps={{
                      "aria-label": `${community.name} CTMHCID`,
                    }}
                    label=""
                    name={`communities.${index}.ctmhcId`}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 220 }}>
                  <FormSelectField
                    control={control}
                    dataTestId={`community-${index}-owner`}
                    id={`community-${index}-owner`}
                    label={undefined}
                    name={`communities.${index}.ownerClientId`}
                    options={ownerOptions}
                    placeholder="Not assigned"
                    onValueChange={(ownerClientId) =>
                      assignOwner(index, ownerClientId ?? "")
                    }
                  />
                </TableCell>
                {isRentManager && (
                  <TableCell sx={{ minWidth: 200 }}>
                    <FormDatePicker
                      control={control}
                      dataTestId={`community-${index}-default-addendum-date`}
                      id={`community-${index}-default-addendum-date`}
                      label=""
                      labelHeight={InputLabelHeight.SMALL}
                      maxDate={new Date(9999, 11, 31)}
                      name={`communities.${index}.defaultAddendumDate`}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
